import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Image as ImageIcon, Edit2, Trash2, ExternalLink } from 'lucide-react';
import api from '../api';

export default function BannerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [banner, setBanner] = useState(null);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadBanner();
    }, [id]);

    const loadBanner = async () => {
        try {
            const data = await api.getAllBannersAdmin();
            const found = data.find(b => b.id === id);
            setBanner(found);
        } catch (error) {
            console.error('Error loading banner:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async () => {
        try {
            await api.updateBanner(id, { is_active: !banner.is_active });
            setBanner({ ...banner, is_active: !banner.is_active });
        } catch (error) {
            alert('Error updating banner: ' + error.message);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;

        try {
            await api.deleteBanner(id);
            navigate('/admin', { state: { activeTab: 'banners' } });
        } catch (error) {
            alert('Error deleting banner: ' + error.message);
        }
    };

    if (loading) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    if (!banner) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h1>Banner not found</h1>
                    <button onClick={() => navigate('/admin', { state: { activeTab: 'banners' } })} className="btn btn-primary" style={{ marginTop: '20px' }}>
                        Back to Banners
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="main" style={{ background: 'var(--bg-admin)', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '1100px', padding: '40px 24px' }}>
                {/* Back Button */}
                <button
                    onClick={() => navigate('/admin', { state: { activeTab: 'banners' } })}
                    className="btn btn-ghost"
                    style={{ marginBottom: '24px', padding: '8px 16px' }}
                >
                    <ArrowLeft size={18} /> Back to Banners
                </button>

                {/* Banner Header Card */}
                <div className="card" style={{ padding: '40px', marginBottom: '24px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '32px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid var(--border)'
                    }}>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '40px',
                                fontWeight: '700',
                                marginBottom: '20px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                                <ImageIcon size={48} />
                            </div>
                            <h1 style={{
                                fontSize: '32px',
                                fontWeight: '700',
                                color: 'var(--text-primary)',
                                marginBottom: '12px'
                            }}>
                                {banner.title}
                            </h1>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleToggleActive}
                                    className={`btn btn-sm ${banner.is_active ? 'btn-success' : 'btn-secondary'}`}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    {banner.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                                    {banner.is_active ? 'Active' : 'Inactive'}
                                </button>
                                <span style={{
                                    display: 'inline-block',
                                    background: 'var(--bg-secondary)',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-secondary)'
                                }}>
                                    Order: {banner.order}
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Link
                                to={`/admin/banners/edit/${banner.id}`}
                                className="btn btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Edit2 size={18} />
                                Edit
                            </Link>
                            <button
                                onClick={handleDelete}
                                className="btn btn-secondary"
                                style={{ color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Trash2 size={18} />
                                Delete
                            </button>
                        </div>
                    </div>

                    {/* Banner Link */}
                    {banner.link && (
                        <div style={{ marginBottom: '24px' }}>
                            <h3 style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: 'var(--text-primary)'
                            }}>
                                Link
                            </h3>
                            <a
                                href={banner.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: 'var(--primary-admin)',
                                    textDecoration: 'none',
                                    fontSize: '15px',
                                    fontWeight: '500'
                                }}
                            >
                                {banner.link}
                                <ExternalLink size={16} />
                            </a>
                        </div>
                    )}
                </div>

                {/* Banner Images */}
                <div className="card" style={{ padding: '40px' }}>
                    <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        marginBottom: '24px',
                        color: 'var(--text-primary)'
                    }}>
                        Banner Images
                    </h3>

                    {/* Desktop Image */}
                    <div style={{ marginBottom: '32px' }}>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-secondary)',
                            marginBottom: '12px'
                        }}>
                            Desktop Image
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                (Recommended: 1920x600px)
                            </span>
                        </div>
                        {banner.desktop_image_url ? (
                            <img
                                src={banner.desktop_image_url}
                                alt={banner.title}
                                style={{
                                    width: '100%',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/1920x600?text=Desktop+Banner';
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '200px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-muted)'
                            }}>
                                No desktop image
                            </div>
                        )}
                    </div>

                    {/* Mobile Image */}
                    <div>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-secondary)',
                            marginBottom: '12px'
                        }}>
                            Mobile Image
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                (Recommended: 800x800px square)
                            </span>
                        </div>
                        {banner.mobile_image_url ? (
                            <img
                                src={banner.mobile_image_url}
                                alt={`${banner.title} - Mobile`}
                                style={{
                                    maxWidth: '400px',
                                    width: '100%',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/800x800?text=Mobile+Banner';
                                }}
                            />
                        ) : (
                            <div style={{
                                maxWidth: '400px',
                                width: '100%',
                                aspectRatio: '1',
                                background: 'var(--bg-secondary)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-muted)'
                            }}>
                                Using desktop image
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

